import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CalendarClock, Plus, Sparkles, AlertTriangle, CheckCircle2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const RISK = { high: "bg-red-100 text-red-700 border-red-200", medium: "bg-amber-100 text-amber-700 border-amber-200", low: "bg-teal-100 text-teal-700 border-teal-200", filled: "bg-slate-100 text-slate-500 border-slate-200" };

export default function Scheduling() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [suggest, setSuggest] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ client_name: "", date: "", start: "09:00", end: "14:00", location: "", services: "" });

  const load = useCallback(() => api.get("/shifts").then((r) => setShifts(r.data.shifts)).catch(() => toast.error("Could not load shifts")), []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    load();
  }, [user, loading, navigate, load]);

  const doSuggest = async (id) => {
    if (suggest[id]) { setSuggest({ ...suggest, [id]: null }); return; }
    try { const { data } = await api.get(`/shifts/${id}/suggest`); setSuggest({ ...suggest, [id]: data.suggestions }); }
    catch { toast.error("Could not get suggestions"); }
  };
  const assign = async (id, caregiverId) => {
    try { await api.post(`/shifts/${id}/assign`, { caregiver_id: caregiverId }); toast.success("Shift filled & caregiver notified"); setSuggest({ ...suggest, [id]: null }); load(); }
    catch { toast.error("Assign failed"); }
  };
  const addShift = async (e) => {
    e.preventDefault();
    try { await api.post("/shifts", form); toast.success("Open shift created"); setShowAdd(false); setForm({ client_name: "", date: "", start: "09:00", end: "14:00", location: "", services: "" }); load(); }
    catch { toast.error("Could not create shift"); }
  };

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="scheduling-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1"><CalendarClock className="w-5 h-5 text-teal-500" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">T5 Scheduling Agent</p></div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">Shifts & coverage</h1>
          <p className="text-slate-600 mt-1">Ranked by fill-risk. The agent flags coverage gaps and suggests eligible caregivers.</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)} data-testid="shift-add-toggle" className="bg-teal-600 hover:bg-teal-700 gap-1.5"><Plus className="w-4 h-4" /> Add shift</Button>
      </div>

      {showAdd && (
        <form onSubmit={addShift} className="mt-5 bg-white border border-slate-200 rounded-2xl p-5 grid sm:grid-cols-3 gap-3" data-testid="shift-add-form">
          <Input required placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="shift-client" />
          <Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="shift-date" />
          <Input placeholder="Location (city, ST)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="shift-location" />
          <Input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="shift-start" />
          <Input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="shift-end" />
          <Input placeholder="Services needed" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} data-testid="shift-services" />
          <Button type="submit" data-testid="shift-add-submit" className="bg-blue-900 hover:bg-blue-950">Create shift</Button>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {shifts.map((s) => (
          <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`shift-${s.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-bold text-blue-950">{s.client_name}</h3>
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${RISK[s.risk.level]}`}>
                    {s.risk.level === "filled" ? "Covered" : `${s.risk.level} risk`}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span>{s.date} · {s.start}–{s.end}</span>
                  {s.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s.location}</span>}
                  {s.services && <span>{s.services}</span>}
                </p>
                <p className="text-xs mt-1 flex items-center gap-1.5 text-slate-500">
                  {s.status === "filled" ? <><CheckCircle2 className="w-3.5 h-3.5 text-teal-600" /> {s.caregiver_name}</> : <><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> {s.risk.note} · {s.eligible_count} eligible caregiver(s)</>}
                </p>
              </div>
              {s.status !== "filled" && (
                <Button onClick={() => doSuggest(s.id)} data-testid={`shift-suggest-${s.id}`} variant="outline" className="gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50"><Sparkles className="w-4 h-4" /> {suggest[s.id] ? "Hide" : "Suggest caregivers"}</Button>
              )}
            </div>
            {suggest[s.id] && (
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-2" data-testid={`shift-suggestions-${s.id}`}>
                {suggest[s.id].map((r) => (
                  <div key={r.caregiver.id} className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center gap-3">
                      <span className="grid place-items-center w-11 h-11 rounded-full bg-blue-950 text-white font-display font-bold text-sm">{r.score}</span>
                      <div><p className="font-semibold text-sm text-blue-950">{r.caregiver.name}</p><p className="text-xs text-slate-500">{r.reasons[0]}</p></div>
                    </div>
                    <Button onClick={() => assign(s.id, r.caregiver.id)} data-testid={`shift-assign-${s.id}-${r.caregiver.id}`} size="sm" className="bg-teal-600 hover:bg-teal-700">Assign</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
